<template>
  <v-navigation-drawer
    v-model="isVisible"
    right
    width="560"
    fixed
    temporary
    class="create-collection-drawer">
    <div class="drawer-content">
      <!-- Header -->
      <div class="drawer-header">
        <div class="header-content">
          <h2 class="text-h5 text-bold drawer-title">
            {{ drawerTitle }}
          </h2>
          <v-btn
            icon
            size="medium"
            variant="text"
            @click="onClose"
            class="close-btn">
            <v-icon size="20">
              mdi-close
            </v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Form Content -->
      <div class="drawer-body">
        <div class="form-fields">
          <!-- Collection Name Field -->
          <div class="form-field">
            <label class="field-label required">
              Collection Name
              <span class="required-asterisk">*</span>
            </label>
            <MankTextField
              v-model="formData.name"
              placeholder="Enter Collection Name"
              :error-messages="errors.name"
              class="form-input"
              hide-details="auto"/>
          </div>

          <!-- Description Field -->
          <div class="form-field">
            <label class="field-label">Description</label>
            <MankTextarea
              v-model="formData.description"
              placeholder="Enter Collection Description"
              variant="outlined"
              hide-details="auto"
              rows="3"/>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="drawer-footer">
        <div class="button-group">
          <v-btn
            color="surface-1"
            size="large"
            elevation="0"
            class="cancel-btn mank-btn"
            @click="onCancel">
            <Icon name="cross" variant="solid" color="default" size="20"/>
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            size="large"
            elevation="0"
            class="apply-btn mank-btn"
            @click="onCreate"
            :loading="loading"
            :disabled="!isFormValid">
            <Icon :name="isEditMode ? 'check' : 'plus'" variant="solid" color="onPrimary" size="20"/>
            {{ submitButtonText }}
          </v-btn>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import CollectionService from '../services/CollectionService';

export default {
  name: 'CreateCollectionDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    collection: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      formData: {
        name: '',
        description: '',
      },
      errors: {
        name: [],
        description: [],
      },
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      },
    },
    isFormValid() {
      return this.formData.name.trim().length > 0;
    },
    isEditMode() {
      return this.collection !== null;
    },
    drawerTitle() {
      return this.isEditMode ? 'Edit Collection' : 'Create Collection';
    },
    submitButtonText() {
      return this.isEditMode ? 'Update' : 'Create';
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
        if (this.isEditMode) {
          this.populateForm();
        }
      }
    },
    collection: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.populateForm();
        }
      },
      deep: true,
    },
  },
  methods: {
    resetForm() {
      this.formData = {
        name: '',
        description: '',
      };
      this.errors = {
        name: [],
        description: [],
      };
      this.loading = false;
    },

    populateForm() {
      if (this.collection) {
        this.formData = {
          name: this.collection.name || '',
          description: this.collection.description || '',
        };
      }
    },
    validateForm() {
      this.errors = {
        name: [],
        description: [],
      };

      // Name validation (required field)
      if (!this.formData.name.trim()) {
        this.errors.name.push('Collection name is required');
        return false;
      }

      if (this.formData.name.trim().length < 2) {
        this.errors.name.push('Collection name must be at least 2 characters');
        return false;
      }

      if (this.formData.name.trim().length > 100) {
        this.errors.name.push('Collection name cannot exceed 100 characters');
        return false;
      }

      // Description validation (optional field)
      if (this.formData.description.trim() && this.formData.description.trim().length > 500) {
        this.errors.description.push('Description cannot exceed 500 characters');
        return false;
      }

      return true;
    },
    onClose() {
      this.$emit('close');
    },
    onCancel() {
      this.resetForm();
      this.$emit('close');
    },
    async onCreate() {
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;

      try {
        const collectionData = {
          name: this.formData.name.trim(),
          description: this.formData.description.trim(),
        };

        let response;
        let successMessage;
        let eventName;

        if (this.isEditMode) {
          response = await CollectionService.updateCollection(this.collection.id, collectionData);
          successMessage = 'Collection updated successfully';
          eventName = 'updated';
        } else {
          response = await CollectionService.createCollection(collectionData);
          successMessage = 'Collection created successfully';
          eventName = 'created';
        }

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: successMessage,
        });

        const collectionResult = this.isEditMode
          ? {
            ...this.collection,
            ...collectionData,
            ...(response.data || response),
            updated_at: new Date().toISOString(),
          }
          : {
            id: response.data?.id || response.id,
            name: collectionData.name,
            description: collectionData.description,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

        this.$emit(eventName, collectionResult);

        this.resetForm();
        this.$emit('close');
      } catch (error) {
        let errorMessage = this.isEditMode
          ? 'An error occurred while updating the collection'
          : 'An error occurred while creating the collection';

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        if (error.response?.status === 400) {
          this.errors.name = [errorMessage];
        } else if (error.response?.status === 409) {
          this.errors.name = ['A collection with this name already exists'];
        } else {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: errorMessage,
          });
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';
@import '../styles/colors.scss';

.create-collection-drawer {
  .drawer-content {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .drawer-header {
    border-bottom: 1px solid $light-border;

    .header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 24px;
      gap: 24px;

      .drawer-title {
        flex: 1;
        color: $light-foreground-1;
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 700;
        font-size: $h5-size;
        line-height: 1.4;
        margin: 0;
      }

      .close-btn {
        background-color: $light-surface-2;
        width: 32px;
        height: 32px;

        &:hover {
          background-color: $light-border;
        }
      }
    }
  }

  .drawer-body {
    flex: 1;
    overflow-y: auto;

    .form-fields {
      display: flex;
      flex-direction: column;
      gap: 24px;
      padding: 24px;
      align-self: stretch;

      .form-field {
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-self: stretch;

        .field-label {
          color: $light-foreground-1;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 600;
          font-size: $body-3-size;
          line-height: 1.2;

          &.required {
            .required-asterisk {
              color: $light-primary;
              font-weight: 700;
              margin-left: 2px;
            }
          }
        }

        .collection-input,
        .collection-textarea {
          background-color: $light-surface-2;

          ::v-deep .v-field__input {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 400;
            font-size: $body-3-size;
            line-height: 1.2;
            color: $light-foreground-1;

            &::placeholder {
              color: $light-foreground-3;
            }
          }

          ::v-deep .v-field__outline {
            display: none;
          }

          ::v-deep .v-field {
            border-radius: 8px;
          }
        }

        .collection-textarea {
          ::v-deep .v-field__input {
            line-height: 1.6;
            min-height: 84px;
          }
        }
      }
    }
  }

  .drawer-footer {
    border-top: 1px solid $light-border;
    background-color: $light-surface-1;
    padding: 12px 24px;
    border-top: 1px solid $light-border;
    flex-shrink: 0;
    .button-group {
      display: flex;
      gap: 12px;
      .cancel-btn,
      .apply-btn {
        flex: 1;
        height: 32px;
      }
    }
  }
}

// V-navigation-drawer specific overrides
::v-deep .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
